import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, LOGO_URL } from "@/lib/api";
import { useAgencyWS, playBeep, speakFR } from "@/hooks/useAgencyWS";
import { useNetworkStatus } from "@/hooks/useNetworkStatus";
import { useI18n, LANGUAGES } from "@/lib/i18n";
import { warnDev } from "@/lib/log";
import Marquee from "react-fast-marquee";
import { Building2, Globe, Maximize2, Minimize2, Volume2, VolumeX, WifiOff, Loader2 } from "lucide-react";

const FRENCH_WEEKDAYS = ["dimanche", "lundi", "mardi", "mercredi", "jeudi", "vendredi", "samedi"];
const FRENCH_MONTHS = ["janvier", "février", "mars", "avril", "mai", "juin", "juillet", "août", "septembre", "octobre", "novembre", "décembre"];

function formatLongDateFR(d) {
  return `${FRENCH_WEEKDAYS[d.getDay()]} ${d.getDate()} ${FRENCH_MONTHS[d.getMonth()]} ${d.getFullYear()}`;
}

/* ============================================================
 * Header — logo + agency + clock + controls
 * ============================================================ */
function TvHeader({ agency, settings, now, lang, setLang, isFullscreen, toggleFullscreen, soundOn, toggleSound, t }) {
  return (
    <header className="flex items-center justify-between px-10 py-5 bg-slate-900/90 backdrop-blur border-b border-slate-800">
      <div className="flex items-center gap-5">
        <img src={LOGO_URL} alt="MAMA" className="w-16 h-16 bg-white rounded-xl p-1.5 shadow-lg" />
        <div>
          <div className="font-display font-black text-4xl text-white tracking-tight leading-none">
            {settings.organization_name || "MAMA"}
          </div>
          <div className="text-base text-slate-300 tracking-widest uppercase mt-1">
            {agency?.name || "—"}{agency?.city ? ` · ${agency.city}` : ""}
          </div>
        </div>
      </div>

      {/* Brand stripes */}
      <div className="hidden xl:flex items-center gap-1.5">
        <div className="h-2.5 w-16 bg-[#DC2626] rounded-sm" />
        <div className="h-2.5 w-16 bg-white rounded-sm" />
        <div className="h-2.5 w-16 bg-[#16A34A] rounded-sm" />
      </div>

      <div className="flex items-center gap-6">
        {/* Lang */}
        <div className="flex items-center gap-1 bg-slate-800/80 rounded-full px-1.5 py-1">
          <Globe className="w-4 h-4 text-slate-400 ml-1.5" />
          {LANGUAGES.map((l) => (
            <button
              key={l.code}
              onClick={() => setLang(l.code)}
              data-testid={`display-lang-${l.code}`}
              className={`px-3 py-1 rounded-full text-sm font-bold tracking-wider transition ${
                lang === l.code ? "bg-[#DC2626] text-white shadow" : "text-slate-300 hover:text-white"
              }`}
            >
              {l.code.toUpperCase()}
            </button>
          ))}
        </div>

        {/* Sound toggle */}
        <button
          onClick={toggleSound}
          data-testid="display-sound-toggle"
          aria-label={soundOn ? t("display.sound_on") : t("display.sound_off")}
          title={soundOn ? t("display.sound_on") : t("display.sound_off")}
          className={`p-3 rounded-xl transition ${
            soundOn ? "bg-[#16A34A]/20 text-[#34D399] hover:bg-[#16A34A]/30"
                    : "bg-slate-800 text-slate-400 hover:bg-slate-700"
          }`}
        >
          {soundOn ? <Volume2 className="w-6 h-6" /> : <VolumeX className="w-6 h-6" />}
        </button>

        {/* Fullscreen toggle */}
        <button
          onClick={toggleFullscreen}
          data-testid="display-fullscreen-toggle"
          aria-label={isFullscreen ? t("display.exit_fullscreen") : t("display.fullscreen")}
          title={isFullscreen ? t("display.exit_fullscreen") : t("display.fullscreen")}
          className="p-3 rounded-xl bg-slate-800 text-slate-300 hover:bg-slate-700 transition"
        >
          {isFullscreen ? <Minimize2 className="w-6 h-6" /> : <Maximize2 className="w-6 h-6" />}
        </button>

        {/* Clock */}
        <div className="text-right" data-testid="display-clock">
          <div className="font-display font-bold text-5xl text-white tabular-nums leading-none">
            {now.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
          </div>
          <div className="text-sm text-slate-400 capitalize mt-1.5">
            {formatLongDateFR(now)}
          </div>
        </div>
      </div>
    </header>
  );
}

/* ============================================================
 * Now Serving block — the giant called ticket
 * ============================================================ */
function NowServingBlock({ current, animateKey, t }) {
  if (!current) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center text-slate-700">
        <div className="font-display font-black text-[200px] leading-none">—</div>
        <div className="mt-6 text-3xl text-slate-500 tracking-wide uppercase">{t("display.no_ticket")}</div>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col justify-center" data-testid="now-serving-block">
      <div className="text-base uppercase tracking-[0.45em] text-[#F87171] font-bold mb-8">
        {t("display.ticket_called")}
      </div>

      <div
        key={animateKey}
        className="tv-ticket-pop font-display font-black text-white tracking-tighter leading-none text-[160px] xl:text-[220px] 2xl:text-[260px] tv-glow"
        style={{ color: "#FFFFFF" }}
      >
        {current.full_ticket_code}
      </div>

      <div className="flex flex-wrap items-center gap-8 mt-10">
        {current.assigned_counter_name && (
          <div className="flex items-baseline gap-4" data-testid="now-serving-counter">
            <span className="text-xl uppercase tracking-[0.3em] text-slate-400">{t("display.counter")}</span>
            <span className="font-display font-black text-6xl xl:text-7xl text-[#16A34A]">
              → {current.assigned_counter_name}
            </span>
          </div>
        )}
      </div>

      <div className="mt-8">
        <div
          className="inline-flex items-center px-8 py-4 rounded-2xl font-display font-bold text-3xl xl:text-4xl border-2"
          style={{
            background: `${current.color}22`,
            color: current.color,
            borderColor: `${current.color}66`,
          }}
          data-testid="now-serving-category"
        >
          {current.category_label}
        </div>
      </div>

      <div className="mt-10 text-xl text-slate-500 tracking-wide">{t("display.please_proceed")}</div>
    </div>
  );
}

/* ============================================================
 * Right column — last called + upcoming by category
 * ============================================================ */
function LastCalledList({ items, t }) {
  return (
    <div data-testid="last-called-list" className="flex flex-col min-h-0">
      <div className="flex items-center justify-between mb-3">
        <div className="text-sm uppercase tracking-[0.4em] text-slate-400 font-bold">{t("display.last_called")}</div>
        <div className="text-xs text-slate-600">{items.length}/5</div>
      </div>
      {items.length === 0 ? (
        <div className="text-center text-slate-600 py-4 italic">{t("display.no_recent_calls")}</div>
      ) : (
        <div className="space-y-2">
          {items.map((it, idx) => (
            <div
              key={`${it.id}-${idx}`}
              className="flex items-center justify-between bg-slate-900/70 border border-slate-800 rounded-xl px-4 py-2.5"
              data-testid={`last-called-${idx}`}
            >
              <div className="font-display font-black text-2xl text-white tabular-nums">{it.full_ticket_code}</div>
              <div className="flex items-center gap-3">
                <div
                  className="px-2.5 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-widest"
                  style={{ background: `${it.color}25`, color: it.color }}
                >
                  {it.category_label}
                </div>
                <div className="font-display font-bold text-lg text-[#34D399] min-w-[80px] text-right">
                  → {it.assigned_counter_name || "—"}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function UpcomingByCategory({ groups, t, totalWaiting }) {
  const shown = groups.slice(0, 5);
  return (
    <div data-testid="upcoming-by-category" className="flex flex-col min-h-0">
      <div className="flex items-center justify-between mb-4">
        <div className="text-sm uppercase tracking-[0.4em] text-slate-400 font-bold">{t("display.upcoming_by_cat")}</div>
        <div className="text-xs text-slate-500">{t("display.waiting_count", { n: totalWaiting })}</div>
      </div>
      {shown.length === 0 ? (
        <div className="text-center text-slate-600 py-6 italic">{t("display.empty_queue")}</div>
      ) : (
        <div className="space-y-2 overflow-hidden">
          {shown.map((g) => (
            <div key={g.code} className="bg-slate-900/60 border border-slate-800 rounded-xl px-4 py-2.5" data-testid={`upcoming-cat-${g.code}`}>
              <div className="flex items-center justify-between mb-1.5">
                <div className="flex items-center gap-2">
                  <div className="w-2.5 h-2.5 rounded-full" style={{ background: g.color }} />
                  <div className="font-display font-bold text-base text-white">{g.label}</div>
                </div>
                <div className="text-xs font-mono text-slate-400">{g.tickets.length}</div>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {g.tickets.slice(0, 5).map((t2) => (
                  <span
                    key={t2.id}
                    className="px-2.5 py-1 rounded-md font-mono font-bold text-base tabular-nums"
                    style={{ background: `${g.color}1F`, color: g.color }}
                  >
                    {t2.full_ticket_code}
                  </span>
                ))}
                {g.tickets.length > 5 && (
                  <span className="px-2.5 py-1 rounded-md font-mono text-sm text-slate-500">
                    +{g.tickets.length - 5}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ============================================================
 * MAIN
 * ============================================================ */
const SOUND_KEY = "mama.display.sound";
const SNAPSHOT_KEY_PREFIX = "mama.display.snapshot.";

export default function DisplayPage() {
  const { agencyId: agencyParam } = useParams();
  const nav = useNavigate();
  const { lang, setLang, t } = useI18n();
  const { online, backendReachable } = useNetworkStatus();

  const [agencies, setAgencies] = useState([]);
  const [agency, setAgency] = useState(null);
  const [queue, setQueue] = useState({ serving: [], waiting: [], waiting_count: 0, completed_count: 0 });
  const [settings, setSettings] = useState({});
  const [displayMessages, setDisplayMessages] = useState([]);
  const [lastCalled, setLastCalled] = useState([]); // up to 5 most recent calls
  const [animateKey, setAnimateKey] = useState(0);
  const [flash, setFlash] = useState(false);
  const [now, setNow] = useState(new Date());
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [soundOn, setSoundOn] = useState(() => localStorage.getItem(SOUND_KEY) !== "0");
  const rootRef = useRef(null);
  const seenServingRef = useRef(new Set()); // ticket IDs already announced

  /* Resolve agency by code OR id */
  useEffect(() => {
    api.get("/agencies/public")
      .then((r) => setAgencies(r.data || []))
      .catch((e) => warnDev("display agencies fetch failed", e));
    api.get("/settings")
      .then((r) => setSettings(r.data || {}))
      .catch((e) => warnDev("display settings fetch failed", e));
    const timer = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!agencyParam || agencies.length === 0) return;
    const lower = String(agencyParam).toLowerCase();
    const a = agencies.find((x) => x.id === agencyParam)
           || agencies.find((x) => String(x.code).toLowerCase() === lower);
    setAgency(a || null);
    // Restore last known snapshot immediately so the screen is never empty
    if (a) {
      try {
        const raw = localStorage.getItem(`${SNAPSHOT_KEY_PREFIX}${a.id}`);
        if (raw) {
          const cached = JSON.parse(raw);
          if (cached?.queue) setQueue(cached.queue);
        }
      } catch (e) { warnDev("snapshot hydrate failed", e); }
    }
  }, [agencyParam, agencies]);

  const agencyId = agency?.id || null;

  /* Configurable scrolling messages */
  useEffect(() => {
    if (!agencyId) return;
    api.get("/display-messages", { params: { agency_id: agencyId } })
      .then((r) => setDisplayMessages(r.data || []))
      .catch((e) => warnDev("display messages fetch failed", e));
  }, [agencyId]);

  /* Queue refresh */
  const refresh = useCallback(async () => {
    if (!agencyId) return;
    try {
      const { data } = await api.get("/tickets/queue", { params: { agency_id: agencyId } });
      setQueue(data);
      // Persist snapshot so a TV that loses connection still shows the last known state
      try {
        localStorage.setItem(`${SNAPSHOT_KEY_PREFIX}${agencyId}`,
          JSON.stringify({ queue: data, savedAt: Date.now() }));
      } catch (e) { warnDev("snapshot save failed", e); }
      // Seed lastCalled from currently-serving tickets (sorted by called_at desc)
      const serving = (data.serving || []).filter((s) => s.full_ticket_code);
      setLastCalled((prev) => {
        if (prev.length > 0) return prev; // already initialized via WS or earlier
        return serving.slice(0, 5);
      });
      serving.forEach((s) => seenServingRef.current.add(s.id));
    } catch (e) {
      warnDev("display queue fetch failed", e);
      // On failure, attempt to hydrate from last known snapshot
      try {
        const raw = localStorage.getItem(`${SNAPSHOT_KEY_PREFIX}${agencyId}`);
        if (raw) {
          const cached = JSON.parse(raw);
          if (cached?.queue) setQueue(cached.queue);
        }
      } catch (snapErr) { warnDev("snapshot restore failed", snapErr); }
    }
  }, [agencyId]);

  useEffect(() => {
    refresh();
    const tm = setInterval(refresh, 15000);
    return () => clearInterval(tm);
  }, [refresh]);

  /* Fullscreen detection */
  useEffect(() => {
    const handler = () => setIsFullscreen(Boolean(document.fullscreenElement));
    document.addEventListener("fullscreenchange", handler);
    return () => document.removeEventListener("fullscreenchange", handler);
  }, []);

  const toggleFullscreen = useCallback(() => {
    const el = rootRef.current;
    if (!el) return;
    if (!document.fullscreenElement) {
      el.requestFullscreen?.().catch((e) => warnDev("fullscreen failed", e));
    } else {
      document.exitFullscreen?.().catch((e) => warnDev("exit fullscreen failed", e));
    }
  }, []);

  const toggleSound = useCallback(() => {
    setSoundOn((s) => {
      const next = !s;
      localStorage.setItem(SOUND_KEY, next ? "1" : "0");
      return next;
    });
  }, []);

  /* WebSocket: ticket events */
  const onWs = useCallback((msg) => {
    if (msg.type === "ticket.created" || msg.type === "ticket.update") {
      refresh();
      return;
    }
    if (msg.type === "ticket.called" || msg.type === "ticket.recall") {
      const ticket = msg.ticket;
      if (!ticket) return;
      refresh();

      // Animate
      setAnimateKey((k) => k + 1);
      setFlash(true);
      setTimeout(() => setFlash(false), 1100);

      // Last-called history (dedupe by id, keep newest first, max 5)
      setLastCalled((prev) => {
        const without = prev.filter((p) => p.id !== ticket.id);
        return [ticket, ...without].slice(0, 5);
      });

      // Sound + voice (only if toggled on)
      if (soundOn) {
        playBeep();
        const counterPart = ticket.assigned_counter_name ? `, ${ticket.assigned_counter_name}` : "";
        const spoken = `Ticket ${(ticket.full_ticket_code || "").replace("-", " ")}${counterPart}`;
        setTimeout(() => speakFR(spoken), 700);
      }
    }
  }, [refresh, soundOn]);

  const wsStatus = useAgencyWS(agencyId, onWs);
  const isDegraded = !online || !backendReachable || wsStatus === "reconnecting";

  /* Upcoming grouped by category */
  const upcomingGroups = useMemo(() => {
    const byCode = new Map();
    (queue.waiting || []).forEach((tk) => {
      const k = tk.category_code || "UNK";
      if (!byCode.has(k)) {
        byCode.set(k, {
          code: k,
          label: tk.category_label || k,
          color: tk.color || "#94A3B8",
          priority: tk.priority_level ?? 0,
          tickets: [],
        });
      }
      byCode.get(k).tickets.push(tk);
    });
    return Array.from(byCode.values()).sort((a, b) => b.priority - a.priority);
  }, [queue.waiting]);

  /* Marquee content — combines tv_marquee + display_messages */
  const marqueeSegments = useMemo(() => {
    const segs = [];
    if (settings.tv_marquee) segs.push(settings.tv_marquee);
    displayMessages.forEach((m) => { if (m.message) segs.push(m.message); });
    if (segs.length === 0) segs.push("Bienvenue à la MAMA — Mutuelle d'Assurance Malagasy. Merci de patienter.");
    segs.push("★ Sécurité • Engagement • Solidarité ★");
    return segs;
  }, [settings.tv_marquee, displayMessages]);

  const currentlyServing = queue.serving?.[0];

  /* === Agency picker === */
  if (!agencyParam) {
    return (
      <div className="min-h-screen bg-slate-950 text-white flex flex-col items-center justify-center p-10">
        <img src={LOGO_URL} alt="MAMA" className="w-24 h-24 mb-8 bg-white rounded-2xl p-2 shadow-2xl" />
        <h1 className="font-display font-black text-5xl mb-3">{t("display.display_screen")}</h1>
        <p className="text-slate-400 mb-12 text-xl">{t("display.select_agency")}</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-6xl w-full">
          {agencies.map((a) => (
            <button
              key={a.id}
              data-testid={`display-agency-${a.code}`}
              onClick={() => nav(`/display/${a.code}`)}
              className="bg-slate-900 border-2 border-slate-800 rounded-2xl p-8 hover:border-[#DC2626] hover:bg-slate-900/60 transition text-left"
            >
              <Building2 className="w-10 h-10 text-[#DC2626] mb-4" />
              <div className="font-display font-black text-3xl text-white">{a.name}</div>
              <div className="text-slate-400 mt-1">{a.city}</div>
              <div className="text-xs font-mono uppercase tracking-widest text-slate-600 mt-3">{a.code}</div>
            </button>
          ))}
        </div>
      </div>
    );
  }

  if (!agency) {
    return (
      <div className="min-h-screen bg-slate-950 text-white flex flex-col items-center justify-center">
        <div className="text-3xl font-display">Agence introuvable</div>
        <button
          onClick={() => nav("/display")}
          className="mt-6 px-6 py-3 bg-[#DC2626] rounded-xl font-bold"
          data-testid="display-back-to-picker"
        >
          Retour
        </button>
      </div>
    );
  }

  return (
    <div
      ref={rootRef}
      className="min-h-screen bg-slate-950 text-white flex flex-col relative overflow-hidden"
      data-testid="tv-display"
    >
      {/* Decorative background grid */}
      <div className="absolute inset-0 pointer-events-none opacity-[0.05]" aria-hidden="true"
        style={{
          backgroundImage:
            "radial-gradient(circle at 20% 20%, rgba(220,38,38,0.4) 0, transparent 35%), radial-gradient(circle at 80% 80%, rgba(22,163,74,0.35) 0, transparent 40%)",
        }}
      />

      <TvHeader
        agency={agency}
        settings={settings}
        now={now}
        lang={lang}
        setLang={setLang}
        isFullscreen={isFullscreen}
        toggleFullscreen={toggleFullscreen}
        soundOn={soundOn}
        toggleSound={toggleSound}
        t={t}
      />

      {/* Resilience badge — last-known-state mode */}
      {isDegraded && (
        <div
          className="absolute top-24 right-6 z-30 px-4 py-2 rounded-full bg-amber-500/90 text-white text-sm font-bold tracking-wide shadow-xl flex items-center gap-2"
          data-testid="tv-degraded-badge"
          role="status"
        >
          {!online || !backendReachable
            ? <><WifiOff className="w-4 h-4" /> Mode dernier état connu</>
            : <><Loader2 className="w-4 h-4 animate-spin" /> Reconnexion…</>
          }
        </div>
      )}

      {/* Flash overlay on new call */}
      {flash && (
        <div className="absolute inset-0 pointer-events-none tv-flash" aria-hidden="true" data-testid="tv-flash" />
      )}

      {/* MAIN GRID */}
      <main className="flex-1 grid grid-cols-1 lg:grid-cols-5 relative">
        {/* Now serving — 60% */}
        <section className="lg:col-span-3 px-14 py-10 flex flex-col border-r border-slate-800">
          <NowServingBlock current={currentlyServing} animateKey={animateKey} t={t} />
        </section>

        {/* Right column — 40% */}
        <aside className="lg:col-span-2 px-8 py-6 flex flex-col gap-5 bg-slate-950/80 overflow-hidden">
          <div className="flex-1 min-h-0 overflow-hidden">
            <UpcomingByCategory groups={upcomingGroups} totalWaiting={queue.waiting_count || 0} t={t} />
          </div>
          <div className="border-t border-slate-800 pt-4 max-h-[40%] overflow-hidden">
            <LastCalledList items={lastCalled} t={t} />
          </div>
        </aside>
      </main>

      {/* Marquee */}
      <footer className="bg-gradient-to-r from-[#7F1D1D] via-[#DC2626] to-[#7F1D1D] text-white py-4 overflow-hidden border-t border-red-900/40">
        <Marquee speed={55} gradient={false} pauseOnHover={false}>
          {marqueeSegments.map((seg, i) => (
            <span key={i} className="mx-14 font-display font-semibold text-2xl tracking-wide">
              {seg}
            </span>
          ))}
        </Marquee>
      </footer>
    </div>
  );
}
